import { useRef, useEffect } from 'react';
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const fmt = v => v >= 1e7 ? `₹${(v/1e7).toFixed(1)}Cr` : v >= 1e5 ? `₹${(v/1e5).toFixed(1)}L` : `₹${Math.round(v).toLocaleString('en-IN')}`;
  return (
    <div style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 12px' }}>
      <p style={{ color: 'var(--text-dim)', fontSize: '11px', marginBottom: '4px' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.stroke, fontSize: '12px', fontWeight: 600 }}>{p.name}: {fmt(p.value)}</p>
      ))}
    </div>
  );
};

function merge(pranav, rashu, huf, combined) {
  const map = {};
  (pranav || []).forEach(d => { map[d.month] = { ...map[d.month], month: d.month, pranav: d.value }; });
  (rashu || []).forEach(d => { map[d.month] = { ...map[d.month], month: d.month, rashu: d.value }; });
  (huf || []).forEach(d => { map[d.month] = { ...map[d.month], month: d.month, huf: d.value }; });
  (combined || []).forEach(d => { map[d.month] = { ...map[d.month], month: d.month, combined: d.value }; });
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month)).map(d => ({
    ...d,
    label: new Date(d.month + 'T00:00:00Z').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
  }));
}

export default function NetworthChart({ pranav, rashu, huf, combined, names = {} }) {
  const data = merge(pranav, rashu, huf, combined);
  const nm = { pranav: names.pranav || 'Pranav', rashu: names.rashu || 'Rashu', huf: 'HUF' };
  const fmt = v => v >= 1e7 ? `${(v/1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(0)}L` : Math.round(v/1e5) + 'L';
  const firstRender = useRef(true);
  useEffect(() => { firstRender.current = false; }, []);
  const anim = firstRender.current;

  return (
    <ResponsiveContainer width="100%" height="100%" minHeight={240}>
      <ComposedChart data={data} margin={{ top: 8, right: 6, left: -14, bottom: 0 }}>
        <defs>
          <linearGradient id="combinedFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34d399" stopOpacity={0.32} />
            <stop offset="55%" stopColor="#34d399" stopOpacity={0.08} />
            <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="label"
          tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 12 }}
          axisLine={false}
          tickLine={false}
          interval={Math.floor(data.length / 5)}
        />
        <YAxis
          tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 12 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={fmt}
          width={48}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: '14px', fontWeight: 500, paddingTop: '10px', color: 'var(--text-dim)' }}
          iconType="line"
        />
        {/* combined as gradient-filled area */}
        <Area type="monotone" dataKey="combined" name="Combined" stroke="#34d399" strokeWidth={2.5} fill="url(#combinedFill)" dot={false} isAnimationActive={anim} animationDuration={1000} animationEasing="ease-out" />
        <Line type="monotone" dataKey="pranav" name={nm.pranav} stroke="#4da6ff" strokeWidth={1.75} dot={false} isAnimationActive={anim} animationDuration={1000} animationEasing="ease-out" />
        <Line type="monotone" dataKey="rashu" name={nm.rashu} stroke="#c084fc" strokeWidth={1.75} dot={false} isAnimationActive={anim} animationDuration={1000} animationEasing="ease-out" />
        <Line type="monotone" dataKey="huf" name={nm.huf} stroke="#fbbf24" strokeWidth={1.75} dot={false} isAnimationActive={anim} animationDuration={1000} animationEasing="ease-out" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
